import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { SUPPORTED_LANGUAGES, Language } from '../i18n';

const LanguageSwitcher: React.FC = () => {
  const { t, currentLanguage, setCurrentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageChange = (language: Language) => {
    setCurrentLanguage(language);
    setIsOpen(false);
  };

  const currentLangInfo = SUPPORTED_LANGUAGES[currentLanguage];

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: 'linear-gradient(135deg, #1e293b, #334155)',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 500,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        }}
      >
        <span style={{ fontSize: 16 }}>{currentLangInfo.flag}</span>
        <span>{currentLangInfo.nativeName}</span>
        <span style={{ fontSize: 12, marginLeft: 4 }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 8,
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            border: '1px solid #e2e8f0',
            minWidth: 200,
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
              {t('language.current')}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
              {currentLangInfo.nativeName} ({currentLangInfo.name})
            </div>
          </div>

          <div style={{ padding: '8px 0' }}>
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code as Language)}
                disabled={code === currentLanguage}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: code === currentLanguage ? '#f8fafc' : 'transparent',
                  border: 'none',
                  cursor: code === currentLanguage ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 14,
                  color: code === currentLanguage ? '#64748b' : '#1e293b',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (code !== currentLanguage) {
                    e.currentTarget.style.background = '#f1f5f9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (code !== currentLanguage) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: 18 }}>{lang.flag}</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 600 }}>{lang.nativeName}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{lang.name}</div>
                </div>
                {code === currentLanguage && (
                  <span style={{ marginLeft: 'auto', color: '#10b981', fontSize: 16 }}>✓</span>
                )}
              </button>
            ))}
          </div>

          <div style={{ 
            padding: '12px 20px', 
            background: '#f8fafc', 
            borderTop: '1px solid #e2e8f0',
            fontSize: 12,
            color: '#64748b',
            textAlign: 'center'
          }}>
            {t('language.switch')}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default LanguageSwitcher;
